import { useMemo } from "react";
import { CanvasTexture, PlaneGeometry, SRGBColorSpace } from "three";

interface SignDecalProps {
  position: [number, number, number];
  rotationY?: number;
  /** Texto corto (p. ej. "SALIDA →", "RECEPCIÓN"). */
  text: string;
  width?: number;
  height?: number;
  /**
   * evac: placa verde de evacuación. info: placa blanca con texto teal.
   * plate: placa institucional teal con texto blanco.
   */
  variant?: "evac" | "info" | "plate";
  /** "wall" (vertical) o "floor" (plano sobre el piso, sin espejar). */
  orientation?: "wall" | "floor";
}

const PALETTES = {
  evac: { bg: "#0d7a3f", fg: "#ffffff", border: "#0a5c30" },
  info: { bg: "#f4f2ec", fg: "#2f6364", border: "#2f6364" },
  plate: { bg: "#2f6364", fg: "#f4f2ec", border: "#24504f" },
} as const;

/**
 * Señalética física sobria: placa plana con texto mate (nada emisivo
 * tipo neón). En el piso la rotación se hornea en la geometría para
 * que el texto nunca salga espejado.
 */
export function SignDecal({
  position,
  rotationY = 0,
  text,
  width = 0.9,
  height = 0.35,
  variant = "evac",
  orientation = "wall",
}: SignDecalProps) {
  const { texture, geometry } = useMemo(() => {
    const canvas = document.createElement("canvas");
    canvas.width = 512;
    canvas.height = Math.max(128, Math.round((512 * height) / width));
    const ctx = canvas.getContext("2d")!;
    const pal = PALETTES[variant];
    ctx.fillStyle = pal.bg;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = pal.border;
    ctx.lineWidth = 10;
    ctx.strokeRect(5, 5, canvas.width - 10, canvas.height - 10);
    ctx.fillStyle = pal.fg;
    const fontPx = Math.min(canvas.height * 0.5, (canvas.width * 1.6) / Math.max(text.length, 4));
    ctx.font = `600 ${fontPx}px sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(text, canvas.width / 2, canvas.height / 2);
    const tex = new CanvasTexture(canvas);
    tex.colorSpace = SRGBColorSpace;
    tex.anisotropy = 4;
    const geo = new PlaneGeometry(width, height);
    if (orientation === "floor") geo.rotateX(-Math.PI / 2);
    return { texture: tex, geometry: geo };
  }, [text, width, height, variant, orientation]);

  return (
    <group position={position} rotation={[0, rotationY, 0]}>
      {/* soporte de la placa (solo en muro) */}
      {orientation === "wall" && (
        <mesh position={[0, 0, -0.015]}>
          <boxGeometry args={[width + 0.06, height + 0.06, 0.03]} />
          <meshStandardMaterial color="#3c4145" roughness={0.5} metalness={0.4} />
        </mesh>
      )}
      <mesh geometry={geometry}>
        <meshStandardMaterial map={texture} roughness={0.85} metalness={0} />
      </mesh>
    </group>
  );
}

/** Luminaria de emergencia: pequeño rectángulo emisivo sobre las salidas. */
export function EmergencyLight({ position, rotationY = 0 }: { position: [number, number, number]; rotationY?: number }) {
  return (
    <mesh position={position} rotation={[0, rotationY, 0]}>
      <boxGeometry args={[0.32, 0.1, 0.06]} />
      <meshStandardMaterial color="#e8e6dd" emissive="#cfe8d4" emissiveIntensity={1.4} />
    </mesh>
  );
}
